import {
    movieCardContentActionsQuery,
    MovieCardContentActions,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';
import { useResources, useSignal } from '@funtech-int/rival-react';
import { useEffect, useRef, useState } from 'react';
import { useAuthManager } from '@funtech-int/yango-shared';

import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';
import { getMovieUGCKey } from '@/shared/lib/movieUGC';
import { useInMemoryCache } from '@/shared/hooks/useInMemoryCache';
import { useSearchParams } from '@/shared/hooks/useSearchParams';
import { QueryParameter } from '@/shared/constants/queryParams';

import { watchingProgressResourceQuery } from '../lib/watchingProgressResource';

interface MovieCardContentActionsVariables {
    contentId: string;
    isAuth: boolean;
    billingFeaturesCount: number;
}

interface GQLQueryParams {
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
    variables: MovieCardContentActionsVariables;
}
const CONTENT_ACTIONS_LIFETIME = 60 * 60 * 1000;
const WATCHING_PROGRESS_LIFETIME = 2 * 1000;

const movieCardContentActionsResourceQuery = createResourceQuery<
    GQLQueryParams,
    MovieCardContentActions | undefined | null
>({
    key(ctx) {
        return [movieCardContentActionsQuery.name, JSON.stringify(ctx.variables)].join(':');
    },
    fetch(ctx) {
        return ctx.client
            .fetch({
                operation: movieCardContentActionsQuery,
                variables: {
                    contentId: ctx.variables.contentId,
                    isAuth: ctx.variables.isAuth,
                },
            })
            .then(({ data }) => {
                if (data) {
                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'plannedToWatch'),
                        Boolean(data.userData?.isPlannedToWatch)
                    );

                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'notInterested'),
                        Boolean(data.userData?.isNotInterested)
                    );

                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'watched'),
                        Boolean(data.userData?.isWatched)
                    );

                    ctx.inMemoryCache.set(
                        getMovieUGCKey(data.id, 'emotion'),
                        data.userData?.emotion ?? null
                    );
                }

                return data;
            });
    },
    toJSON(data) {
        return data as any;
    },
    hydrate(data: any) {
        return movieCardContentActionsQuery.hydrate(data) as any;
    },
});

interface UserMovieCardContentActionsQueryParams {
    contentId: string;
}

function useKeyByPlayingContentId() {
    const [key, setKey] = useState(0);
    const params = useSearchParams();

    const playingContentId = params.get(QueryParameter.PLAYING_CONTENT_ID);

    const prevPlayingContentId = useRef<string | null>();

    useEffect(() => {
        if (typeof prevPlayingContentId.current === 'string' && !playingContentId) {
            setKey((prev) => prev + 1);
        }

        prevPlayingContentId.current = playingContentId;
    }, [playingContentId]);

    return key;
}

export function useMovieCardContentActions(params: UserMovieCardContentActionsQueryParams) {
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const billingFeaturesCount = useSignal(useProfileManager().billingFeatureNames).length;
    const key = useKeyByPlayingContentId();
    const { isAuthorized } = useAuthManager();

    const [contentActions, watchingProgress] = useResources(
        movieCardContentActionsResourceQuery(
            {
                client,
                inMemoryCache,
                variables: {
                    contentId: params.contentId,
                    billingFeaturesCount,
                    isAuth: isAuthorized,
                },
            },
            { lifetime: CONTENT_ACTIONS_LIFETIME }
        ),
        ...(isAuthorized
            ? [
                  watchingProgressResourceQuery(
                      {
                          client,
                          variables: {
                              contentId: params.contentId,
                              billingFeaturesCount,
                              key,
                          },
                      },
                      { lifetime: WATCHING_PROGRESS_LIFETIME }
                  ),
              ]
            : [])
    );
    return { contentActions, watchingProgress };
}
